import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileDown, ShieldCheck, AlertTriangle, X } from "lucide-react";
import { ContractDocument } from "@/components/contract/ContractDocument";
import { SignaturePad } from "@/components/contract/SignaturePad";
import { CameraCapture } from "@/components/contract/CameraCapture";
import { IdCapture } from "@/components/contract/IdCapture";
import { Field } from "@/components/admin/EditorControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const REP_TITLES = ["代表取締役", "代表取締役社長", "代表取締役会長", "取締役", "執行役員", "代表者", "支配人", "その他"];

export default function ContractSign() {
  const { token } = useParams();
  const [tpl, setTpl] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState("");
  const [repName, setRepName] = useState("");
  const [repTitle, setRepTitle] = useState("");
  const [repTitleOther, setRepTitleOther] = useState("");
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [sending, setSending] = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    Promise.all([api.get("/contract/template"), api.get(`/contract/${token}`)])
      .then(([t, c]) => {
        setTpl(t.data);
        setData(c.data);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  const effectiveTitle = repTitle === "その他" ? repTitleOther.trim() : repTitle;

  const STEPS = [
    { title: "ご担当者情報の入力", valid: !!(address.trim() && effectiveTitle && repName.trim() && phone.trim()), msg: "住所・代表者の肩書・氏名・会社の電話番号をすべて入力してください" },
    { title: "手書きサイン", valid: !!signature, msg: "枠内にサインをお書きください" },
    { title: "担当者の写真", valid: !!photo, msg: "担当者の写真を撮影（または画像選択）してください" },
    { title: "身分証明書の撮影", valid: !!(idFront && idBack), msg: "身分証明書の表面と裏面を両方撮影してください" },
    { title: "同意・締結", valid: true, msg: "" },
  ];

  const openWizard = () => {
    const firstIncomplete = STEPS.findIndex((s) => !s.valid);
    setStep(firstIncomplete === -1 ? 5 : firstIncomplete + 1);
    setWizardOpen(true);
  };

  const nextStep = () => {
    const s = STEPS[step - 1];
    if (!s.valid) {
      toast.error(s.msg);
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/contract/${token}/sign`, { address, rep_name: repName, title: effectiveTitle, phone, agreed, signature, photo, id_front: idFront, id_back: idBack });
      toast.success("契約が締結されました");
      const c = await api.get(`/contract/${token}`);
      setData(c.data);
      setJustSigned(true);
      setWizardOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const pdfUrl = `${API}/contract/${token}/pdf`;

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="contract-sign-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-2xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight">
            <img src="/prc-logo.png" alt="" className="h-7 w-7 object-contain" />PRC<span className="text-gold"> REMIT</span>
          </Link>
          <span className="text-xs text-white/40">電子契約</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-32">
        {!data && !error && (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
        )}

        {error && (
          <div className="py-24 text-center" data-testid="contract-error">
            <AlertTriangle className="mx-auto text-red-400 mb-4" size={32} />
            <p className="text-white/70">{error}</p>
          </div>
        )}

        {data && (
          <>
            {data.status === "signed" ? (
              <div className="mb-6 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="signed-banner">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="text-emerald-300" size={22} />
                  <div>
                    <p className="font-jp font-bold text-emerald-200">この契約は締結済みです</p>
                    <p className="text-xs text-emerald-200/60 mt-0.5">締結日時: {data.signer?.signed_at_jp || data.signer?.signed_at}</p>
                  </div>
                </div>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="download-pdf"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
                >
                  <FileDown size={16} /> 契約書PDFをダウンロード
                </a>
              </div>
            ) : (
              <div className="mb-6 rounded-xl border border-gold/30 bg-gold/[0.06] p-5" data-testid="pending-banner">
                <p className="font-jp font-bold text-gold">契約内容をご確認の上、ページ下部のボタンから署名手続きを開始してください</p>
                <p className="text-xs text-white/50 mt-1">署名いただくと契約が締結され、締結日時・IPアドレス等の記録が電磁的記録として保存されます（本契約書第31条に基づく電子契約）。</p>
              </div>
            )}

            <ContractDocument
              template={tpl?.template}
              ctx={data.ctx}
              signer={data.signer}
              signatureB64={data.signature_b64}
              photoB64={data.photo_b64}
              idFrontB64={data.id_front_b64}
              idBackB64={data.id_back_b64}
              sealB64={data.seal_b64}
              showEvidence={false}
            />

            {data.status !== "signed" && (
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-10 text-center" data-testid="sign-form">
                <h2 className="font-jp text-lg font-bold">甲（委託者）署名手続き</h2>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">
                  下のボタンを押すとポップアップが開き、<br className="sm:hidden" />
                  情報入力 → サイン → 担当者写真 → 身分証明書 → 同意 の順に進みます。
                </p>
                <button
                  onClick={openWizard}
                  data-testid="wizard-open"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-base font-bold text-ink hover:bg-gold-light transition-colors"
                >
                  <ShieldCheck size={18} /> 署名手続きを開始する
                </button>
              </div>
            )}

            {wizardOpen && data.status !== "signed" && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="wizard-modal">
                <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono tracking-[0.25em] text-gold" data-testid="wizard-step-label">STEP {step} / 5</span>
                    <button onClick={() => setWizardOpen(false)} data-testid="wizard-close" className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
                  </div>
                  <div className="flex gap-1.5 mb-6">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <span key={n} className={`h-1 flex-1 rounded-full transition-colors ${n <= step ? "bg-gold" : "bg-white/10"}`} />
                    ))}
                  </div>
                  <h3 className="font-jp text-lg font-bold mb-6">{STEPS[step - 1].title}</h3>

                  {step === 1 && (
                    <div className="space-y-4" data-testid="wizard-step1">
                      <Field label="住所" value={address} onChange={setAddress} testid="sign-address" placeholder="例：東京都千代田区〇〇1-2-3" />
                      <div>
                        <span className="block text-xs text-white/50 mb-1.5">代表者の肩書</span>
                        <Select value={repTitle} onValueChange={setRepTitle}>
                          <SelectTrigger className="w-full bg-white/[0.03] border-white/10 text-white" data-testid="sign-rep-title">
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                          <SelectContent>
                            {REP_TITLES.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {repTitle === "その他" && (
                          <input
                            value={repTitleOther}
                            onChange={(e) => setRepTitleOther(e.target.value)}
                            data-testid="sign-rep-title-other"
                            placeholder="肩書を入力してください"
                            className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors"
                          />
                        )}
                      </div>
                      <Field label="代表者氏名" value={repName} onChange={setRepName} testid="sign-rep-name" placeholder="例：山田 太郎" />
                      <Field label="会社の電話番号" value={phone} onChange={setPhone} testid="sign-phone" placeholder="例：059-000-0000" />
                    </div>
                  )}
                  {step === 2 && <div data-testid="wizard-step2"><SignaturePad onChange={setSignature} /></div>}
                  {step === 3 && <div data-testid="wizard-step3"><CameraCapture onChange={setPhoto} /></div>}
                  {step === 4 && (
                    <div className="space-y-6" data-testid="wizard-step4">
                      <IdCapture label="身分証明書（表面）の写真" side="front" onChange={setIdFront} testid="id-front" />
                      <IdCapture label="身分証明書（裏面）の写真" side="back" onChange={setIdBack} testid="id-back" />
                    </div>
                  )}
                  {step === 5 && (
                    <div className="space-y-5" data-testid="wizard-step5">
                      <p className="text-sm text-white/60 leading-relaxed">
                        契約内容とご入力いただいた情報をご確認の上、同意にチェックを入れて締結ボタンを押してください。
                        締結日時・IPアドレス等の記録が電磁的記録として保存されます（本契約書第31条に基づく電子契約）。
                      </p>
                      <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          data-testid="agree-checkbox"
                          className="mt-1 h-4 w-4 accent-[#35e0c8]"
                        />
                        <span className="text-sm text-white/80">
                          上記の契約内容をすべて確認し、電磁的記録による電子契約として締結することに同意します。
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="mt-8 flex items-center gap-3">
                    {step > 1 && (
                      <button onClick={() => setStep(step - 1)} data-testid="wizard-back" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">
                        戻る
                      </button>
                    )}
                    {step < 5 ? (
                      <button onClick={nextStep} data-testid="wizard-next" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors">
                        完了（次へ進む）
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={!agreed || sending}
                        data-testid="sign-submit"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-base font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {sending ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                        同意して契約を締結する
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {justSigned === false && data.status === "signed" && null}
          </>
        )}
      </main>
    </div>
  );
}
